import { useAuth0 } from '@auth0/auth0-react'
import type React from 'react'
import { useSelector } from 'react-redux'
import penningSymbol from '../../../../assets/symbol.png'
import { AUTH0_LOGOUT_URL } from '../../../../constants'
import { useGetUserQuery } from '../../../../redux/services/coreApi'
import { clearImpersonationSnapshot } from '../../../../redux/store'
import type { RootState } from '../../../../redux/store'
import VeliOrganizationVatModal from './components/VeliOrganizationVatModal'

type Props = {
  children: React.ReactNode
}

// Wraps the onboarding screen in a stripped-down, Penning × Veli frame — but
// only for a client who arrived via the Veli activation flow (issue #76,
// ADR-0004). During the interim (before cutover) the rest of the platform is
// hidden for these clients — no sidebar, no locked "coming soon" surfaces — so
// the screen is a focused landing page that says one thing clearly: finish your
// setup, then sit tight until we move you. Everything they need lives here, so
// this minimal header carries the only chrome that survives: the co-brand mark
// and a way to log out.
//
// Veli origin is read from the signed-in user (server-authoritative, ADR-0004),
// so the frame is just as present after a logout/login as straight off
// activation. The wrapped children are the *identical* standard onboarding
// screen — a non-Veli client gets the children verbatim, unchanged.
const VeliOnboardingShell: React.FC<Props> = ({ children }) => {
  const sessionUser = useSelector((state: RootState) => state.session.user)
  const { data: user } = useGetUserQuery(sessionUser.id)
  const { logout } = useAuth0()

  if (!user?.isVeliOrigin) {
    return <>{children}</>
  }

  return (
    // Own the screen height and a single scroll context so the page scrolls as
    // one beneath the fixed header (the wrapped screen renders embedded — natural
    // height, no scroll of its own).
    <div className="h-full overflow-y-auto bg-bg-base">
      {/* Co-brand mark sits on its own above the card — centred, with the one-line
          reassurance — so the page reads as a calm landing, not a chromed app
          surface. Log out is the only other control, tucked top-right so it's
          always reachable without competing with the brand. */}
      <div className="relative max-w-3xl mx-auto px-4">
        <button
          type="button"
          onClick={() => {
            clearImpersonationSnapshot()
            logout({ returnTo: AUTH0_LOGOUT_URL })
          }}
          className="absolute right-4 top-5 text-sm text-description hover:text-black transition-colors"
        >
          Log out
        </button>
        <div className="flex flex-col items-center pt-12 pb-1 text-center">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 flex items-center justify-center shrink-0 bg-[#9F9F92] rounded-full">
              <img src={penningSymbol} alt="Penning logo" className="h-5" />
            </div>
            <p className="text-2xl font-semi">
              Penning{' '}
              <span className="text-description font-normal">× Veli</span>
            </p>
          </div>
          <p className="text-description mt-3">
            A few quick steps before the move.
          </p>
        </div>
      </div>
      {children}
      {/* A corporate client whose account was created as an individual (the Veli export carried no
          company columns) owes us the VAT number their record never held, before their account can be
          corrected to a company account. Blocking, and self-clearing once supplied. */}
      {user.requiresOrganizationVatNumber && <VeliOrganizationVatModal />}
    </div>
  )
}

export default VeliOnboardingShell
